import { Header } from 'components/Header'
import { PaymentCCContainer } from './styled'
import { useEffect, useState } from 'react'
import { RenderIf } from 'components/RenderIf'
import { Overwiew } from './Overview'
import { LoginSuggestion } from './LoginSuggestion'
import { PaymentSteps, ResultParams, ResultType } from './types'
import { Credentials } from './Credentials'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { Result } from './Result'
import { EmptyBlock } from '../CheckoutQRM/styled'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import { findCatalogProduct } from 'featureSliced/shared/utils/findCatalogProduct'
import { findTemplateUnavailableLines } from 'featureSliced/shared/utils/templateDays'
import { findSoldOutLines } from 'featureSliced/shared/utils/soldOut'
import {
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'
import { isPickupTimeAllowed } from 'featureSliced/shared/utils/getEarliestPickup'
import moment from 'moment'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import { useWebLayout } from 'featureSliced/shared/hooks/useWebLayout'
import { CheckoutWeb } from 'featureSliced/widgets/WebLayout/CheckoutWeb'

export const PaymentCC = () => {
  const navigate = useNavigate()
  const dispatch = useMyDispatch()
  const t = useTranslations()
  const [searchParams] = useSearchParams()
  const isUserAuth = useMySelector((state) => state.auth.isLoggedIn)
  const bucketProducts = useMySelector((state) => state.bucket.products)
  const productList = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const soldOutIds = useMySelector((state) => state.categories.soldOutIds)
  const isProductsLoaded = useMySelector((state) => state.app.isProductsLoaded)
  const clickCollectConfig = useMySelector(
    (state) => state.app.clickCollectConfig
  )

  const { isWeb } = useWebLayout()

  // QRM-641: the web layout shows the order recap next to the form for the whole
  // checkout, so the cart-review step has nothing left to do — a guest still gets
  // the account suggestion first, exactly as handleOverwiewNextClick decides on
  // the phone. Nothing else about the flow changes.
  const [step, setStep] = useState<PaymentSteps>(
    isWeb
      ? isUserAuth
        ? PaymentSteps.CREDENTIALS
        : PaymentSteps.SUGGESTION
      : PaymentSteps.OVEWVIEW
  )
  const [requestResult, setRequestResult] = useState<ResultType>('unknown')
  const [orderNr, setOrderNr] = useState<number>()

  useEffect(() => {
    if (!bucketProducts?.length) {
      navigate(getNavLink(searchParams, 'menu'))
    }
  }, [bucketProducts])

  // A reload or deep link on /payment never mounts the menu, whose own cleanup would
  // drop sold-out lines — do it here as soon as the sold-out list is known (QRM-667).
  // The cart stays on screen with whatever is left; an empty cart already goes back
  // to the menu above.
  useEffect(() => {
    if (!isProductsLoaded || !bucketProducts?.length) return
    const soldOutLines = findSoldOutLines(bucketProducts, soldOutIds)
    if (!soldOutLines.length) return
    soldOutLines.forEach((line) =>
      dispatch(QRMBucketSlice.actions.removeProductFromBucket(line.uniqueId))
    )
    dispatch(
      ToastsSlice.actions.openToast({
        message: t('products.someProductsAreSoldOut') || '',
        type: 'warning',
      })
    )
  }, [isProductsLoaded, soldOutIds, bucketProducts]) // eslint-disable-line

  // Backstop re-check on the way into checkout: the menu-load cleanup (Menu/index.tsx)
  // covers the common path, but a customer can also reach PaymentCC via the "Continue to
  // checkout" button from an already-loaded menu whose pickup time/catalogue changed
  // underneath the persisted cart. Remove what no longer qualifies and send the customer
  // back to the menu rather than let them pay for it (QRM-639).
  useEffect(() => {
    if (!isProductsLoaded || !bucketProducts?.length) return
    const { pickUpTime } = handleLocalStorage(['pickUpTime'])
    // Company-level rule first: if the stored pickup time no longer satisfies the
    // shop's lead-days/cutoff at all (config raised, or the date slipped into the
    // past while the tab sat open), no line in the cart can be ordered — clear it
    // and send the customer back to the menu, whose own guard reopens the time
    // wizard for a fresh pick.
    if (
      !isPickupTimeAllowed({
        now: moment(),
        pickUpTime,
        leadTimeDays: clickCollectConfig?.leadTimeDays,
        cutoff: clickCollectConfig?.leadTimeHours,
      })
    ) {
      dispatch(QRMBucketSlice.actions.clearBucket())
      dispatch(
        ToastsSlice.actions.openToast({
          message: t('clickCollect.leadDaysItemsRemoved'),
          type: 'error',
        })
      )
      navigate(getNavLink(searchParams, 'menu'))
      return
    }
    const exceededLines = bucketProducts.filter((line) => {
      const catalogProduct = findCatalogProduct(productList, line.productId)
      if (!catalogProduct) return false
      const leadDays = getItemLeadDays(catalogProduct.ClickCollectLeadDaysCount)
      return isItemLeadDaysExceeded({ leadDays, pickUpTime })
    })
    if (exceededLines.length) {
      exceededLines.forEach((line) =>
        dispatch(QRMBucketSlice.actions.removeProductFromBucket(line.uniqueId))
      )
      dispatch(
        ToastsSlice.actions.openToast({
          message: t('clickCollect.leadDaysItemsRemoved'),
          type: 'error',
        })
      )
      navigate(getNavLink(searchParams, 'menu'))
      return
    }

    // A template can close for the chosen pickup day between adding an item and
    // reaching checkout (QRM-648) — the currently loaded menu already reflects
    // that day (products_v2 is fetched with pickUpTime), so presence is enough.
    const templateUnavailableLines = findTemplateUnavailableLines(
      bucketProducts,
      productList
    )
    if (templateUnavailableLines.length) {
      templateUnavailableLines.forEach((line) =>
        dispatch(QRMBucketSlice.actions.removeProductFromBucket(line.uniqueId))
      )
      dispatch(
        ToastsSlice.actions.openToast({
          message: t('products.someProductsNotAvailable'),
          type: 'error',
        })
      )
      navigate(getNavLink(searchParams, 'menu'))
    }
  }, [isProductsLoaded]) // eslint-disable-line

  const handleOverwiewNextClick = () => {
    if (isUserAuth) {
      setStep(PaymentSteps.CREDENTIALS)
    } else {
      setStep(PaymentSteps.SUGGESTION)
    }
  }

  const moveToResult = (result: ResultType, orderNumberToSet?: number) => {
    setOrderNr(orderNumberToSet)
    setRequestResult(result)
    setStep(PaymentSteps.RESULT)
  }

  const steps = (
    <>
      <RenderIf condition={step === PaymentSteps.SUGGESTION}>
        <LoginSuggestion
          moveToCredentials={() => setStep(PaymentSteps.CREDENTIALS)}
        />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.CREDENTIALS}>
        <Credentials moveToResult={moveToResult} />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.RESULT}>
        <Result result={requestResult} orderNr={orderNr} />
      </RenderIf>
    </>
  )

  if (isWeb) {
    return (
      <CheckoutWeb
        withSummary={step !== PaymentSteps.RESULT}
        title={step === PaymentSteps.RESULT ? undefined : t('buttons.checkout')}
      >
        {steps}
      </CheckoutWeb>
    )
  }

  return (
    <PaymentCCContainer>
      <Header />
      <EmptyBlock />

      <RenderIf condition={step === PaymentSteps.OVEWVIEW}>
        <Overwiew moveToSuggestions={handleOverwiewNextClick} />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.SUGGESTION}>
        <LoginSuggestion
          moveToCredentials={() => setStep(PaymentSteps.CREDENTIALS)}
        />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.CREDENTIALS}>
        <Credentials moveToResult={moveToResult} />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.RESULT}>
        <Result result={requestResult} orderNr={orderNr} />
      </RenderIf>
    </PaymentCCContainer>
  )
}
